import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download, Refresh } from '@mui/icons-material';
import { taxBooksService } from '../../../services/api';

const formatCell = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return value;
};

const TaxBookPage = ({
  title,
  subtitle,
  previewPath,
  exportPath,
  columns,
  summaryCards = [],
  extraFilters = [],
  sharedFilters = null,
  hideFilterCard = false,
  reloadToken = undefined,
  hideRefreshButton = false,
  downloadLabel = 'Descargar CSV',
}) => {
  const now = new Date();
  const [localFilters, setLocalFilters] = useState({
    month: String(now.getMonth() + 1),
    year: String(now.getFullYear()),
    documentType: '',
    branchId: '',
    status: '',
  });
  const effectiveFilters = sharedFilters ? { ...localFilters, ...sharedFilters } : localFilters;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sharedSignature = sharedFilters ? JSON.stringify(sharedFilters) : '';

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const branchRows = await taxBooksService.getCatalog('branches');
        setBranches(branchRows || []);
      } catch {
        setBranches([]);
      }
    };

    loadCatalogs();
  }, []);

  const loadPreview = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await taxBooksService.getPreview(previewPath, effectiveFilters);
      setRows(response.rows || []);
      setSummary(response.summary || {});
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudo cargar la vista previa');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sharedFilters) return;
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedSignature]);

  useEffect(() => {
    if (reloadToken === undefined) return;
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken]);

  const handleExport = async () => {
    try {
      const blob = await taxBooksService.exportCsv(exportPath, effectiveFilters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const month = String(effectiveFilters.month).padStart(2, '0');
      link.href = url;
      link.download = `${exportPath.replace(/\//g, '_')}_${effectiveFilters.year}_${month}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudo exportar el CSV');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!hideRefreshButton && (
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadPreview} disabled={loading}>
              Actualizar
            </Button>
          )}
          <Button variant="contained" startIcon={<Download />} onClick={handleExport} disabled={loading}>
            {downloadLabel}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!hideFilterCard && (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Mes"
                value={effectiveFilters.month}
                onChange={(event) => setLocalFilters((prev) => ({ ...prev, month: event.target.value }))}
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <MenuItem key={index + 1} value={String(index + 1)}>
                    {String(index + 1).padStart(2, '0')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Año"
                value={effectiveFilters.year}
                onChange={(event) => setLocalFilters((prev) => ({ ...prev, year: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={effectiveFilters.branchId}
                onChange={(event) => setLocalFilters((prev) => ({ ...prev, branchId: event.target.value }))}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">Todas</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name} ({branch.codigo_establecimiento})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {extraFilters.map((filter) => (
              <Grid item xs={12} md={2} key={filter.key}>
                <TextField
                  select={Array.isArray(filter.options)}
                  fullWidth
                  label={filter.label}
                  value={effectiveFilters[filter.key] ?? ''}
                  onChange={(event) => setLocalFilters((prev) => ({ ...prev, [filter.key]: event.target.value }))}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  {(filter.options || []).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ))}
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={loadPreview} disabled={loading} sx={{ height: '100%' }}>
                Generar vista previa
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      )}

      {summaryCards.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} md={4} key={card.key}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h5">
                    {card.format ? card.format(summary[card.key]) : formatCell(summary[card.key] ?? 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      {loading ? 'Cargando...' : 'No hay datos para el período seleccionado'}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row, index) => (
                  <TableRow key={`${row.id || row.codigo_generacion || index}-${index}`}>
                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        {column.render ? column.render(row[column.key], row) : formatCell(row[column.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TaxBookPage;
